package br.com.htms.inspeccaocampo.platform

import br.com.htms.inspeccaocampo.util.Base64Codec
import kotlin.coroutines.resume
import kotlin.js.Date
import kotlin.js.unsafeCast
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import org.khronos.webgl.Uint8Array
import org.khronos.webgl.get
import org.w3c.dom.CanvasLineCap
import org.w3c.dom.CanvasLineJoin
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event
import org.w3c.files.FileReader
import org.w3c.files.get

actual object PlatformServices {
    actual val hasCamera: Boolean get() = true
    actual val hasGps: Boolean get() = true

    actual fun openUrl(url: String) {
        window.open(url, "_blank")
    }

    actual suspend fun captureGps(): GpsFix? {
        val geo = js("navigator.geolocation")
        if (geo == null || geo == undefined) return null
        // Desktop/Wi‑Fi: alta precisão costuma falhar — tenta normal e depois alta precisão.
        return requestPosition(geo, highAccuracy = false, timeoutMs = 20000, maximumAgeMs = 60000)
            ?: requestPosition(geo, highAccuracy = true, timeoutMs = 25000, maximumAgeMs = 0)
    }

    private suspend fun requestPosition(
        geo: dynamic,
        highAccuracy: Boolean,
        timeoutMs: Int,
        maximumAgeMs: Int
    ): GpsFix? = suspendCancellableCoroutine { cont ->
        try {
            val opts = js("{}")
            opts.enableHighAccuracy = highAccuracy
            opts.timeout = timeoutMs
            opts.maximumAge = maximumAgeMs
            geo.getCurrentPosition(
                { pos ->
                    try {
                        val coords = pos.coords
                        val lat = (coords.latitude as Number).toDouble()
                        val lng = (coords.longitude as Number).toDouble()
                        if (cont.isActive) {
                            cont.resume(
                                GpsFix(
                                    latitude = lat,
                                    longitude = lng,
                                    textoLocal = formatGpsCoordenadas(lat, lng)
                                )
                            )
                        }
                    } catch (_: Throwable) {
                        if (cont.isActive) cont.resume(null)
                    }
                },
                { _ ->
                    if (cont.isActive) cont.resume(null)
                },
                opts
            )
        } catch (_: Throwable) {
            if (cont.isActive) cont.resume(null)
        }
    }

    actual suspend fun capturePhoto(): CapturedPhoto? = pickOrCapture(capture = true)

    actual suspend fun pickImage(): CapturedPhoto? = pickOrCapture(capture = false)

    private suspend fun pickOrCapture(capture: Boolean): CapturedPhoto? =
        suspendCancellableCoroutine { cont ->
            val input = document.createElement("input") as HTMLInputElement
            input.type = "file"
            // Preferir JPEG da câmera (evita HEIC do iPhone que o canvas não decodifica).
            input.accept = if (capture) "image/jpeg,image/png,image/*" else "image/jpeg,image/png,image/webp,image/*"
            if (capture) {
                input.setAttribute("capture", "environment")
            }
            input.style.display = "none"
            document.body?.appendChild(input)

            fun cleanup() {
                runCatching { document.body?.removeChild(input) }
            }

            input.onchange = { _: Event ->
                val file = input.files?.get(0)
                if (file != null) {
                    val reader = FileReader()
                    reader.onload = {
                        val result = reader.result
                        val buffer = result as? org.khronos.webgl.ArrayBuffer
                        if (buffer == null) {
                            cleanup()
                            if (cont.isActive) cont.resume(null)
                        } else {
                            val uint8 = Uint8Array(buffer)
                            val bytes = ByteArray(uint8.length) { i -> uint8[i] }
                            val path = PlatformFiles.join(
                                PlatformFiles.documentsDir(),
                                "fotos/web_${Date().getTime().toLong()}.jpg"
                            )
                            PlatformFiles.ensureDir(PlatformFiles.join(PlatformFiles.documentsDir(), "fotos"))
                            GlobalScope.launch {
                                try {
                                    val jpeg = PlatformImage.toJpegBytes(bytes)
                                    if (jpeg == null || jpeg.isEmpty()) {
                                        val heic = isLikelyHeic(bytes)
                                        consoleWarn(
                                            if (heic) "Captura HEIC sem conversão — use a câmera (JPEG) ou foto já em JPG/PNG"
                                            else "Captura: não foi possível converter para JPEG"
                                        )
                                        if (cont.isActive) cont.resume(null)
                                    } else {
                                        val ok = PlatformFiles.writeBytesDurable(path, jpeg)
                                        if (!ok) {
                                            consoleWarn("IDB falhou na captura; usando memória")
                                        }
                                        if (cont.isActive) cont.resume(CapturedPhoto(path = path))
                                    }
                                } catch (t: Throwable) {
                                    consoleWarn("Captura falhou: ${t.message}")
                                    if (cont.isActive) cont.resume(null)
                                } finally {
                                    cleanup()
                                }
                            }
                        }
                        Unit
                    }
                    reader.onerror = {
                        cleanup()
                        if (cont.isActive) cont.resume(null)
                    }
                    reader.readAsArrayBuffer(file)
                } else {
                    cleanup()
                    if (cont.isActive) cont.resume(null)
                }
            }
            input.oncancel = {
                cleanup()
                if (cont.isActive) cont.resume(null)
            }
            input.click()
        }

    actual fun encodeSignaturePngBase64(
        strokes: List<List<Pair<Float, Float>>>,
        width: Int,
        height: Int
    ): String? {
        if (width <= 0 || height <= 0 || strokes.isEmpty()) return null
        val canvas = document.createElement("canvas") as HTMLCanvasElement
        canvas.width = width
        canvas.height = height
        val ctx = canvas.getContext("2d") as? CanvasRenderingContext2D ?: return null
        ctx.fillStyle = "#FFFFFF"
        ctx.fillRect(0.0, 0.0, width.toDouble(), height.toDouble())
        ctx.strokeStyle = "#000000"
        ctx.lineWidth = 5.0
        ctx.lineCap = "round".unsafeCast<CanvasLineCap>()
        ctx.lineJoin = "round".unsafeCast<CanvasLineJoin>()
        strokes.forEach { stroke ->
            if (stroke.size < 2) return@forEach
            ctx.beginPath()
            ctx.moveTo(stroke.first().first.toDouble(), stroke.first().second.toDouble())
            stroke.drop(1).forEach { p ->
                ctx.lineTo(p.first.toDouble(), p.second.toDouble())
            }
            ctx.stroke()
        }
        // JPEG facilita embutir no PDF (DCTDecode); PNG quebrava a conversão no mobile.
        val dataUrl = canvas.toDataURL("image/jpeg", 0.92)
        val b64 = dataUrl.substringAfter("base64,", "")
        return b64.ifBlank { null }
    }

    actual fun decodeBase64(base64: String): ByteArray? = Base64Codec.decode(base64)

    actual fun encodeBase64(bytes: ByteArray): String = Base64Codec.encode(bytes)

    actual suspend fun loadImageBytes(path: String): ByteArray? {
        // 1) memória / alias / localStorage
        PlatformFiles.readBytes(path)?.takeIf { it.isNotEmpty() }?.let {
            consoleWarn("loadImageBytes: hit memória ($path, ${it.size}b)")
            return it
        }
        // 2) IndexedDB on-demand (warmUp pode ter falhado ou valor era Blob)
        PlatformFiles.readBytesAsync(path)?.takeIf { it.isNotEmpty() }?.let {
            consoleWarn("loadImageBytes: hit IDB async ($path, ${it.size}b)")
            return it
        }
        // 3) R2 / URL remota (path do form OU alias remoto)
        val remoteCandidates = buildList {
            if (R2Photos.isRemotePath(path)) add(path)
            PlatformFiles.aliasOf(path)?.takeIf { R2Photos.isRemotePath(it) }?.let { add(it) }
        }
        for (remotePath in remoteCandidates) {
            val remote = R2Photos.fetchBytes(remotePath)
            if (remote != null && remote.isNotEmpty()) {
                PlatformFiles.writeBytesDurable(path, remote)
                if (remotePath != path) {
                    PlatformFiles.writeBytesDurable(remotePath, remote)
                    PlatformFiles.linkAlias(path, remotePath)
                }
                consoleWarn("loadImageBytes: hit R2 ($remotePath, ${remote.size}b)")
                return remote
            }
            consoleWarn("loadImageBytes: falha R2/remoto para $remotePath")
        }
        consoleWarn("loadImageBytes: SEM BYTES para $path")
        return null
    }

    actual suspend fun loadBundledAsset(fileName: String): ByteArray? {
        val name = fileName.trim().trimStart('/')
        if (name.isBlank() || name.contains("..")) return null
        return suspendCancellableCoroutine { cont ->
            try {
                val promise: dynamic = js("fetch")(name)
                promise.then(
                    { response ->
                        val ok = (response.ok as? Boolean) == true
                        if (!ok) {
                            if (cont.isActive) cont.resume(null)
                        } else {
                            response.arrayBuffer().then(
                                { buf ->
                                    val u = org.khronos.webgl.Uint8Array(buf as org.khronos.webgl.ArrayBuffer)
                                    val out = ByteArray(u.length) { i -> u[i] }
                                    if (cont.isActive) cont.resume(out)
                                },
                                { if (cont.isActive) cont.resume(null) }
                            )
                        }
                        null
                    },
                    { if (cont.isActive) cont.resume(null); null }
                )
            } catch (_: Throwable) {
                if (cont.isActive) cont.resume(null)
            }
        }
    }

    /**
     * Persiste a foto carimbada (memória + IndexedDB await) e sobe ao R2 quando possível.
     * Se R2 ok: devolve a URL remota (como antes do download-fix) para o PDF poder
     * buscar via rede mesmo se o cache local sumir. Mantém alias local ↔ URL.
     * Se R2 falhar: mantém o path local.
     */
    actual suspend fun publishPhoto(localPath: String): String {
        val bytes = PlatformFiles.readBytes(localPath)
            ?: PlatformFiles.readBytesAsync(localPath)
        if (bytes == null || bytes.isEmpty()) {
            consoleWarn("publishPhoto: sem bytes em $localPath")
            return localPath
        }
        val durableOk = PlatformFiles.writeBytesDurable(localPath, bytes)
        if (!durableOk) {
            consoleWarn("publishPhoto: IndexedDB falhou; mantendo só memória ($localPath)")
        } else {
            consoleWarn("publishPhoto: IDB ok ($localPath, ${bytes.size}b)")
        }
        val url = R2Photos.upload(bytes)
        if (url.isNullOrBlank()) {
            consoleWarn("publishPhoto: R2 indisponível; path local $localPath")
            return localPath
        }
        PlatformFiles.writeBytesDurable(url, bytes)
        PlatformFiles.linkAlias(localPath, url)
        consoleWarn("publishPhoto: R2 ok, path remoto no form ($localPath ↔ $url)")
        // URL remota: loadImageBytes consegue fetch mesmo após limpar cache da sessão.
        // conteudoBase64 no FotoAnexo cobre o caso offline / R2 expirado.
        return url
    }

    private fun consoleWarn(msg: String) {
        try {
            js("console").warn("[ICD Services] $msg")
        } catch (_: Throwable) {
        }
    }

    private fun isLikelyHeic(bytes: ByteArray): Boolean =
        bytes.size >= 12 &&
            bytes[4] == 'f'.code.toByte() &&
            bytes[5] == 't'.code.toByte() &&
            bytes[6] == 'y'.code.toByte() &&
            bytes[7] == 'p'.code.toByte() &&
            (bytes[8] == 'h'.code.toByte() || bytes[8] == 'm'.code.toByte())

    actual suspend fun saveBannerImage(bytes: ByteArray): String? {
        val path = PlatformFiles.join(PlatformFiles.documentsDir(), "app_config/banner_custom.jpg")
        PlatformFiles.ensureDir(PlatformFiles.join(PlatformFiles.documentsDir(), "app_config"))
        val jpeg = PlatformImage.toJpegBytes(bytes) ?: bytes
        PlatformFiles.writeBytes(path, jpeg)
        return path
    }

    actual fun shouldEmbedPhotoBytesInForm(): Boolean = true
}
